// AI CONTEXT: tax-calculation - manual tax calculator fallback
// Reasoning: Provides immediate tax calculation while Stripe automatic tax is being configured
// This can be used as a temporary solution until account-level setup is complete

/**
 * Manual tax calculation utility
 * Provides tax calculation based on US state tax rates
 * Used as fallback when Stripe automatic tax is not configured
 */

// US State Sales Tax Rates (as of 2025)
// Source: https://taxfoundation.org/state-sales-tax-rates-2024/
// Updated Utah rate to 8.25% as of 2025
const STATE_TAX_RATES = {
  'AL': 0.04, // Alabama
  'AK': 0.00, // Alaska (no state sales tax)
  'AZ': 0.056, // Arizona
  'AR': 0.065, // Arkansas
  'CA': 0.0725, // California
  'CO': 0.029, // Colorado
  'CT': 0.0635, // Connecticut
  'DE': 0.00, // Delaware (no state sales tax)
  'FL': 0.06, // Florida
  'GA': 0.04, // Georgia
  'HI': 0.04, // Hawaii
  'ID': 0.06, // Idaho
  'IL': 0.0625, // Illinois
  'IN': 0.07, // Indiana
  'IA': 0.06, // Iowa
  'KS': 0.065, // Kansas
  'KY': 0.06, // Kentucky
  'LA': 0.0445, // Louisiana
  'ME': 0.055, // Maine
  'MD': 0.06, // Maryland
  'MA': 0.0625, // Massachusetts
  'MI': 0.06, // Michigan
  'MN': 0.06875, // Minnesota
  'MS': 0.07, // Mississippi
  'MO': 0.04225, // Missouri
  'MT': 0.00, // Montana (no state sales tax)
  'NE': 0.055, // Nebraska
  'NV': 0.0685, // Nevada
  'NH': 0.00, // New Hampshire (no state sales tax)
  'NJ': 0.06625, // New Jersey
  'NM': 0.05125, // New Mexico
  'NY': 0.04, // New York
  'NC': 0.0475, // North Carolina
  'ND': 0.05, // North Dakota
  'OH': 0.0575, // Ohio
  'OK': 0.045, // Oklahoma
  'OR': 0.00, // Oregon (no state sales tax)
  'PA': 0.06, // Pennsylvania
  'RI': 0.07, // Rhode Island
  'SC': 0.06, // South Carolina
  'SD': 0.045, // South Dakota
  'TN': 0.07, // Tennessee
  'TX': 0.0625, // Texas
  'UT': 0.0825, // Utah
  'VT': 0.06, // Vermont
  'VA': 0.053, // Virginia
  'WA': 0.065, // Washington
  'WV': 0.06, // West Virginia
  'WI': 0.05, // Wisconsin
  'WY': 0.04, // Wyoming
};

/**
 * Calculate tax based on subtotal and state
 * @param {number} subtotal - The subtotal amount in dollars
 * @param {string} state - The two-letter state code (e.g., 'CA', 'NY')
 * @returns {number} The calculated tax amount
 */
export const calculateTax = (subtotal, state) => {
  if (!subtotal || subtotal <= 0) return 0;
  
  const stateCode = state?.toUpperCase();
  const taxRate = STATE_TAX_RATES[stateCode] || 0;
  
  return Math.round(subtotal * taxRate * 100) / 100; // Round to 2 decimal places
};

/**
 * Get tax rate for a specific state
 * @param {string} state - The two-letter state code
 * @returns {number} The tax rate as a decimal (e.g., 0.085 for 8.5%)
 */
export const getTaxRate = (state) => {
  const stateCode = state?.toUpperCase();
  return STATE_TAX_RATES[stateCode] || 0;
};

/**
 * Format tax rate as percentage
 * @param {string} state - The two-letter state code
 * @returns {string} The formatted tax rate (e.g., "8.5%")
 */
export const getTaxRatePercentage = (state) => {
  const rate = getTaxRate(state);
  return rate > 0 ? `${(rate * 100).toFixed(1)}%` : '0%';
};

/**
 * Check if a state has sales tax
 * @param {string} state - The two-letter state code
 * @returns {boolean} True if the state has sales tax
 */
export const hasSalesTax = (state) => {
  const stateCode = state?.toUpperCase();
  return (STATE_TAX_RATES[stateCode] || 0) > 0;
};

/**
 * Get all states with their tax rates
 * @returns {Object} Object with state codes as keys and tax rates as values
 */
export const getAllTaxRates = () => {
  return { ...STATE_TAX_RATES };
};

/**
 * Calculate total including tax
 * @param {number} subtotal - The subtotal amount
 * @param {string} state - The two-letter state code
 * @returns {number} The total including tax
 */
export const calculateTotalWithTax = (subtotal, state) => {
  const tax = calculateTax(subtotal, state);
  return subtotal + tax;
};

const taxCalculator = {
  calculateTax,
  getTaxRate,
  getTaxRatePercentage,
  hasSalesTax,
  getAllTaxRates,
  calculateTotalWithTax,
};

export default taxCalculator;
